import { action, atom } from '@reatom/framework'
import { match } from 'ts-pattern'

import { TokenModerationStatus } from '@blade/blockchain-types'
import { AsyncStateN, Result } from '@blade/std-lib'
import { CurrencyModel } from '@root/entities/currency'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { pairRegistry } from '@root/entities/pair/atoms'
import { fetchCurrencyPairsRequestAction } from '@root/entities/pair/atoms/pair-requests'
import { PairAddress } from '@root/entities/pair/model/PairAddress'
import { Provider } from '@root/entities/provider'

import type { Ctx } from '@reatom/framework'
import type { CurrencyAddress } from '@root/entities/currency/model/CurrencyAddress'
import type { PairModel } from '@root/entities/pair'
import type { Error } from '@root/shared'
import type { ServicesApi } from '@root/shared/api'

export const loadPoolReatom = () => {
  const poolsStateAtom = atom(
    AsyncStateN.notInitiated<Error.UnknownError, Array<PairModel>>(),
    'poolsStateAtom',
  )

  const loadPoolsAction = action(
    async (ctx, inCurrency: CurrencyModel, outCurrency: CurrencyModel) => {
      const network = ctx.get(selectedNetworkAtom)

      const request = prepareRequest(ctx, network, inCurrency, outCurrency)
      if (request.type === 'NoPools') {
        poolsStateAtom(ctx, AsyncStateN.success([]))
        return
      }

      if (request.type === 'Static') {
        // TODO: Wait for proper jupiter support
        // const result = [request.pool]
        // poolsStateAtom(ctx, AsyncStateN.success(result))
        poolsStateAtom(ctx, AsyncStateN.success([]))
        return
      }

      poolsStateAtom(ctx, AsyncStateN.loading())
      const result = await ctx.schedule(() =>
        fetchCurrencyPairsRequestAction(ctx, {
          currencyAddress: request.tokenAddress,
          network: network,
        }),
      )
      const filteredResult = result.pipe(
        Result.map((it) =>
          it.map((dto) => pairRegistry.resolveFromDto(ctx, dto)),
        ),
        Result.map((it) =>
          it.filter((pair) =>
            isValidPool(ctx, { pair, inCurrency, outCurrency }),
          ),
        ),
      )
      poolsStateAtom(ctx, AsyncStateN.fromResult(filteredResult))
    },
    'loadPoolsAction',
  )

  const resetPoolAction = action((ctx) => {
    poolsStateAtom(ctx, AsyncStateN.notInitiated())
  }, 'resetPoolAction')

  return {
    poolsStateAtom,
    loadPoolsAction,
    resetPoolAction,
  }
}

type PrepareResult =
  | {
      type: 'Load'
      tokenAddress: CurrencyAddress
    }
  | {
      type: 'Static'
      pool: PairModel
    }
  | { type: 'NoPools' }

const prepareRequest = (
  ctx: Ctx,
  network: Network.Network,
  baseCurrency: CurrencyModel,
  quoteCurrency: CurrencyModel,
): PrepareResult => {
  return match(network)
    .returnType<PrepareResult>()
    .with(Network.Solana, (): PrepareResult => {
      // If one of the currencies is native, retrieving pools
      if (CurrencyModel.isSolOrWsol(baseCurrency)) {
        return {
          type: 'Load',
          tokenAddress: quoteCurrency.address,
        }
      }
      if (CurrencyModel.isSolOrWsol(quoteCurrency)) {
        return {
          type: 'Load',
          tokenAddress: baseCurrency.address,
        }
      }

      const jupiterPoolDto = createJupiterPoolDto(ctx, {
        base: baseCurrency,
        quote: quoteCurrency,
      })
      const pair = pairRegistry.resolveFromDto(ctx, jupiterPoolDto)

      // Both currencies are not native, provider will be Jupiter
      return {
        type: 'Static',
        pool: pair,
      }
    })
    .with(Network.Ethereum, () => {
      // Currently, in EVM, swap will always involve eth
      if (CurrencyModel.isEthOrWeth(baseCurrency)) {
        return {
          type: 'Load',
          tokenAddress: quoteCurrency.address,
        }
      }
      if (CurrencyModel.isEthOrWeth(quoteCurrency)) {
        return {
          type: 'Load',
          tokenAddress: baseCurrency.address,
        }
      }
      return {
        type: 'NoPools',
      }
    })
    .with(Network.Base, () => {
      // Currently, in EVM, swap will always involve eth
      if (CurrencyModel.isBaseEthOrWeth(baseCurrency)) {
        return {
          type: 'Load',
          tokenAddress: quoteCurrency.address,
        }
      }
      if (CurrencyModel.isBaseEthOrWeth(quoteCurrency)) {
        return {
          type: 'Load',
          tokenAddress: baseCurrency.address,
        }
      }
      return {
        type: 'NoPools',
      }
    })
    .with(Network.Bsc, () => {
      // Currently, in EVM, swap will always involve eth
      if (CurrencyModel.isBnbOrWbnb(baseCurrency)) {
        return {
          type: 'Load',
          tokenAddress: quoteCurrency.address,
        }
      }
      if (CurrencyModel.isBnbOrWbnb(quoteCurrency)) {
        return {
          type: 'Load',
          tokenAddress: baseCurrency.address,
        }
      }
      return {
        type: 'NoPools',
      }
    })
    .with(Network.Ton, () => {
      // Currently, in EVM, swap will always involve eth
      if (CurrencyModel.isTon(baseCurrency)) {
        return {
          type: 'Load',
          tokenAddress: quoteCurrency.address,
        }
      }
      if (CurrencyModel.isTon(quoteCurrency)) {
        return {
          type: 'Load',
          tokenAddress: baseCurrency.address,
        }
      }
      return {
        type: 'NoPools',
      }
    })
    .exhaustive()
}

const isValidPool = (
  ctx: Ctx,
  args: {
    pair: PairModel
    inCurrency: CurrencyModel
    outCurrency: CurrencyModel
  },
) => {
  const extraDetails = ctx.get(args.pair.extraDetails)
  if (
    extraDetails &&
    extraDetails.provider === Provider.pumpFun.id &&
    extraDetails.migration?.migratedToPairAddress
  ) {
    return false
  }

  const inCurrency = CurrencyModel.toNativeIfWrapped(args.inCurrency)
  const outCurrency = CurrencyModel.toNativeIfWrapped(args.outCurrency)
  const baseCurrency = CurrencyModel.toNativeIfWrapped(args.pair.base)
  const quoteCurrency = CurrencyModel.toNativeIfWrapped(args.pair.quote)

  if (
    !CurrencyModel.eq(inCurrency, baseCurrency) &&
    !CurrencyModel.eq(inCurrency, quoteCurrency)
  ) {
    return false
  }

  // eslint-disable-next-line sonarjs/prefer-single-boolean-return
  if (
    !CurrencyModel.eq(outCurrency, baseCurrency) &&
    !CurrencyModel.eq(outCurrency, quoteCurrency)
  ) {
    return false
  }

  return true
}

const createJupiterPoolDto = (
  ctx: Ctx,
  {
    base,
    quote,
  }: {
    base: CurrencyModel
    quote: CurrencyModel
  },
): ServicesApi.PairDto => {
  const emptyPeriodStats: ServicesApi.PairDto['periodStats']['1D'] = {
    txs: 0,
    buys: 0,
    sellers: 0,
    volumeInUsd: '0',
    buyVolumeInUsd: '0',
    sellVolumeInUsd: '0',
    makers: 0,
    buyers: 0,
    startPrice: '0',
    endPrice: '0',
    sells: 0,
    startDate: new Date(0).toISOString(),
    endDate: new Date(0).toISOString(),
    priceChangePercents: '0',
    holdersChangePercents: '0',
    volumeChangePercents: '0',
    netVolumeInUsd: '0',
    updatedAt: new Date(0).toISOString(),
  }

  return {
    fees: {
      totalFees: '0',
      totalNetworkFees: '0',
      totalPriorityFees: '0',
      totalBundleTipFees: '0',
    },
    audit: {
      checks: {
        isFreezable: {
          status: 'Safe',
          value: true,
        },
        isMintable: {
          status: 'Safe',
          value: true,
        },
        updateAuthority: {
          status: 'Safe',
          value: '',
        },
        top10HoldersPercentage: {
          status: 'Safe',
          percent: 0,
          amount: 0,
        },
        ownerTokenBalance: {
          status: 'Safe',
          percent: 0,
          amount: 0,
        },
        lpBurned: {
          status: 'Safe',
          percent: 0,
          amount: 0,
        },
        isTokenDataMutable: {
          status: 'Safe',
          value: true,
        },
      },
      meta: {
        lpCreatorAddress: '',
        deployerAddress: '',
      },
      overAllChecks: {
        overall: 'Safe',
        safeCount: 0,
        warningCount: 0,
        dangerCount: 0,
      },
    },
    firstBuyers: [],
    holders: {
      count: '0',
      top10Holding: '0',
    },
    insiders: {
      holding: '0',
      count: '0',
    },
    snipers: {
      holding: '0',
      count: '0',
    },
    bundlers: {
      holding: '0',
      count: '0',
    },
    priceChart: { interval: '1M', entries: [] },
    premiumInfo: {
      boosters: 0,
      expiresAt: new Date(0).toISOString(),
      premium: false,
      splashPoints: 0,
      tier: undefined,
      premiumId: '',
      status: 'inactive',
      updatedAt: new Date(0).toISOString(),
    },
    priceNative: '0',
    priceUsd: '0',
    liquidityNative: '0',
    liquidityUsd: '0',
    marketCapUsd: '0',
    periodStats: {
      '5M': emptyPeriodStats,
      '1H': emptyPeriodStats,
      '6H': emptyPeriodStats,
      '1D': emptyPeriodStats,
      ALL: emptyPeriodStats,
    },
    extraDetails: undefined,
    initTimestamp: new Date(0).toISOString(),
    network: Network.toDto(base.network),
    pair: [base.address, quote.address],
    poolAddress: PairAddress(
      `jupiter-mock-pool-${base.address}-${quote.address}- ${base.network}`,
    ),
    provider: 'jupiter',
    updatedAt: new Date(0).toISOString(),
    base: {
      address: base.address,
      symbol: base.ticker,
      name: base.title,
      logo: ctx.get(base.icon) ?? '',
      decimals: base.decimals,
      supply: base.supply.toString(),
      network: Network.toDto(base.network),
      status: TokenModerationStatus.Actve,
    },
    quote: {
      address: quote.address,
      symbol: quote.ticker,
      name: quote.title,
      logo: ctx.get(quote.icon) ?? '',
      decimals: quote.decimals,
      supply: quote.supply.toString(),
      network: Network.toDto(quote.network),
      status: TokenModerationStatus.Actve,
    },
    liquidityToken: '',
    allTimeHigh: {
      marketCap: '0',
      price: '0',
    },
  }
}
